import EmailIcon from '@mui/icons-material/Email'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { Box, Typography } from '@mui/material'
import { Stack } from '@mui/system'
import { useEffect, useRef } from 'react'
import { useBranch } from '../../context/branch_context'
import { ClientDocumentType } from '../../context/client_context'
import { useConfigs } from '../../context/configs_context'
import { PaymentInSale, SaleInfo } from '../../context/sale_context'
import {
    getDateString,
    maskCurrency,
    numeroPorExtensoEspanhol,
} from '../../utils/maks'

interface PaymentInSaleForNote extends PaymentInSale {}

interface PaymentNoteProps {
    payment: PaymentInSaleForNote
    showLegalData: boolean
}

const ContactIcon: Record<string, JSX.Element> = {
    Facebook: <FacebookIcon />,
    Instagram: <InstagramIcon />,
    Whatsapp: <WhatsAppIcon />,
    Endereço: <LocationOnIcon />,
    Telefone: <LocalPhoneIcon />,
    Email: <EmailIcon />,
}

function PaymentNote({ payment, showLegalData }: PaymentNoteProps) {
    const { configs, contactTypes, base64Logo } = useConfigs()
    const { branches } = useBranch()

    const contacts = payment.sale?.branch
        ? payment.sale?.branch.contacts
        : configs.contacts
    const companyName =
        payment.sale?.branch && configs.showBranchNameInNote
            ? payment.sale?.branch.name
            : configs.companyName
    const companyLogo = payment.sale?.branch?.companyLogo
        ? branches.find((b) => b.id === payment.sale?.branch?.id)?.companyLogo
              ?.content
        : base64Logo
    const companySlogan = payment.sale?.branch?.companySlogan
        ? payment.sale?.branch.companySlogan
        : configs.companySlogan

    const feesPayed = configs.removeFeesFromReceipt ? payment.feesPayed : 0
    const totalEntry =
        payment.amountPayed +
        payment.discount +
        payment.creditDiscount -
        feesPayed

    const getSales = () => {
        const sales: { sale: SaleInfo; amountPayed: number; date: string }[] =
            []

        if (payment.sale)
            sales.push({
                sale: payment.sale,
                amountPayed: totalEntry,
                date: getDateString({
                    date: payment.sale?.date,
                    hour: payment.sale?.hour,
                    configs: configs,
                    isSummerTime: !!payment.sale?.isSummerTime,
                }),
            })
        if (payment.prevPayments)
            sales.push(
                ...payment.prevPayments.map((p) => {
                    const feesPayed = configs.removeFeesFromReceipt
                        ? p.feesPayed
                        : 0

                    return {
                        sale: p.sale as SaleInfo,
                        amountPayed:
                            p.amountPayed +
                            p.discount +
                            p.creditDiscount -
                            feesPayed,
                        date: getDateString({
                            date: p.sale?.date,
                            hour: p.sale?.hour,
                            configs: configs,
                            isSummerTime: !!p.sale?.isSummerTime,
                        }),
                    }
                })
            )

        return sales
    }

    const coin = !payment.exchange ? payment.paymentCoin : payment.sale?.coin
    const client = payment.prevPayments?.length
        ? payment.prevPayments[0].sale?.client
        : payment.sale?.client

    return (
        <Stack direction="column" width="100%" px="20px" py="10px">
            <Stack direction={'row'} width={'100%'}>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRight={'0'}
                    borderRadius={'12px'}
                    width={'70%'}
                >
                    <Stack direction={'row'} p={'10px'}>
                        <Box
                            component={'img'}
                            src={companyLogo}
                            width={'220px'}
                            height={'110px'}
                            zIndex={'-1'}
                        />
                        <Stack textAlign={'center'} flex={'1'} px={'15px'}>
                            <Typography
                                fontSize={'14px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {companyName}
                            </Typography>
                            <Typography
                                fontSize={'9px'}
                                sx={{ wordBreak: 'break-word' }}
                                fontStyle={'italic'}
                                whiteSpace={'pre-line'}
                            >
                                {`${companySlogan}`}
                            </Typography>
                            <Typography
                                fontSize={'8px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {contactTypes
                                    .filter((ct) =>
                                        contacts.find(
                                            (c) => c.contactType.id === ct.id
                                        )
                                    )
                                    .map(
                                        (ct) =>
                                            `${ct.name}: ${contacts
                                                .filter(
                                                    (c) =>
                                                        c.contactType.id ===
                                                        ct.id
                                                )
                                                .map((c) => c.value)
                                                .join(', ')}`
                                    )
                                    .join('; ')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRadius={'12px'}
                    width={'30%'}
                >
                    <Stack direction={'row'} p={'10px'} textAlign={'center'}>
                        <Box flex={'1'}>
                            <Typography fontSize={'18px'} mt={'3px'}>
                                RECIBO DE DINERO
                            </Typography>
                            {payment.bill && showLegalData ? (
                                <>
                                    <Typography
                                        fontSize={'14px'}
                                        mt={'3px'}
                                        fontWeight={'bold'}
                                    >
                                        RUC:{' '}
                                        {payment.sale?.branch
                                            ? payment.sale.branch
                                                  .companyDocument
                                            : configs.companyDocument}
                                    </Typography>
                                    <Typography
                                        fontSize={'18px'}
                                        mt={'1px'}
                                        fontWeight={'bold'}
                                    >
                                        {payment.bill}
                                    </Typography>
                                </>
                            ) : (
                                <Typography fontSize={'16px'} mt={'3px'}>
                                    Nro.: {payment.id}
                                </Typography>
                            )}
                            <Typography
                                fontSize={'22px'}
                                mt={'3px'}
                                fontWeight={'bold'}
                            >
                                {coin?.symbol} {maskCurrency(totalEntry, coin)}
                            </Typography>
                        </Box>
                        {/* <Typography
                            sx={{
                                writingMode: 'vertical-rl',
                                textOrientation: 'mixed',
                            }}
                            p={'30px'}
                            color={'error'}
                            fontSize={'26px'}
                        >
                            {payment.sale?.bill}
                        </Typography> */}
                    </Stack>
                </Stack>
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderRadius={'12px'}
                direction={'column'}
                padding={'10px'}
                mt={'10px'}
            >
                <Stack direction={'row'} alignItems={'center'}>
                    <Box width={'70%'}>
                        <Typography fontSize={'16px'} fontWeight={'bold'}>
                            Nombre: {client?.name || '---'}
                        </Typography>
                        <Typography fontSize={'16px'} fontWeight={'bold'}>
                            {client?.documentType === ClientDocumentType.RUC
                                ? 'Ruc'
                                : 'Documento'}
                            : {client?.document || '---'}
                        </Typography>
                    </Box>
                    <Box>
                        <Box>
                            <Typography fontSize={'11px'}>
                                Fecha:{' '}
                                {payment.noteDate
                                    ? getDateString({
                                          date: payment.noteDate,
                                          hour: payment.noteHour,
                                          isSummerTime:
                                              payment.isNoteSummerTime ?? false,
                                          configs: configs,
                                      })
                                    : getDateString({
                                          date: payment.date,
                                          hour: payment.hour,
                                          isSummerTime:
                                              payment.isSummerTime ?? false,
                                          configs: configs,
                                      })}
                            </Typography>
                            <Typography fontSize={'11px'}>
                                Telefonos:{' '}
                                {client?.phoneNumbers.join(', ') || '---'}
                            </Typography>
                            <Typography fontSize={'11px'}>
                                Dirección: {client?.address || '---'}
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderRadius={'12px'}
                direction={'column'}
                justifyContent={'space-between'}
                p={'10px'}
                mt={'10px'}
            >
                <Typography fontWeight={'bold'}>
                    En concepto del pago de las facturas:
                </Typography>
                <Stack
                    direction={'row'}
                    flexWrap={'wrap'}
                    mt={'5px'}
                    width={'100%'}
                >
                    {getSales().map((sale) => (
                        <Stack
                            direction={'row'}
                            width={'50%'}
                            justifyContent={'space-between'}
                            mt={'2px'}
                            px={'5px'}
                        >
                            <Typography
                                fontSize={'10px'}
                                width={'30%'}
                                fontWeight={'bold'}
                            >
                                {sale.sale?.bill ?? sale.sale?.id}
                            </Typography>
                            <Typography fontSize={'10px'}>
                                {sale.date.split(' ')[0]}
                            </Typography>
                            <Typography
                                fontSize={'10px'}
                                textAlign={'right'}
                                width={'30%'}
                            >
                                {maskCurrency(
                                    sale.amountPayed,
                                    sale.sale?.coin
                                )}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
                <Typography mt={'10px'} fontWeight={'bold'}>
                    Nota(s) de crédito:
                </Typography>
                <Stack direction={'row'} flexWrap={'wrap'} mt={'5px'}>
                    {getSales().flatMap((s) => {
                        const sale = s.sale

                        return (
                            sale?.creditNotes?.map((creditNote) => (
                                <Stack
                                    direction={'row'}
                                    width={'50%'}
                                    justifyContent={'space-between'}
                                    mt={'2px'}
                                    px={'5px'}
                                >
                                    <Typography
                                        fontSize={'10px'}
                                        width={'30%'}
                                        fontWeight={'bold'}
                                    >
                                        {creditNote.bill ?? creditNote.id}
                                    </Typography>
                                    <Typography fontSize={'10px'}>
                                        {
                                            getDateString({
                                                date: creditNote?.date,
                                                hour: creditNote?.hour,
                                                configs: configs,
                                                isSummerTime:
                                                    !!creditNote?.isSummerTime,
                                            }).split(' ')[0]
                                        }
                                    </Typography>
                                    <Typography
                                        fontSize={'10px'}
                                        textAlign={'right'}
                                    >
                                        -
                                        {maskCurrency(
                                            creditNote.total,
                                            sale?.coin
                                        )}
                                    </Typography>
                                </Stack>
                            )) ?? <></>
                        )
                    })}
                </Stack>
            </Stack>

            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderRadius={'12px'}
                p={'10px'}
                mt={'10px'}
            >
                <Typography fontSize={'14px'} mb={'3px'}>
                    Detalles del pago
                </Typography>
                <Stack width={'100%'} direction={'row'} spacing={'80px'}>
                    <Typography fontSize={'11px'}>Capital: 0</Typography>
                    <Typography fontSize={'11px'}>
                        Interés: {maskCurrency(0, coin)}
                    </Typography>
                    <Typography fontSize="16px" fontWeight="bold">
                        Entrega: {maskCurrency(totalEntry, coin)}
                    </Typography>
                </Stack>
                <Typography fontSize={'11px'}>
                    En letras:{' '}
                    {numeroPorExtensoEspanhol(totalEntry, coin ?? undefined)}
                </Typography>
                {!!client?.id && (
                    <Typography fontSize={'11px'}>
                        Deuda total: {client.coin?.symbol}{' '}
                        {maskCurrency(client?.creditUsed ?? 0, client.coin)}
                    </Typography>
                )}
            </Stack>
            <Stack
                width={'100%'}
                flex={'1'}
                direction={'column'}
                justifyContent={'flex-end'}
            >
                <Stack
                    direction={'row'}
                    justifyContent={'space-evenly'}
                    width={'100%'}
                    p={'3px'}
                >
                    <Typography fontSize={'12px'}>Sello</Typography>
                    <Typography fontSize={'12px'}>Firma</Typography>
                </Stack>
                <Box width="100%" borderBottom="1px solid black" />
            </Stack>
        </Stack>
    )
}

interface Props {
    payments: PaymentInSaleForNote[]
    handleClose(): void
    dontPrint?: boolean
    showLegalData: boolean
}

export function PaymentNoteDialog({
    handleClose,
    payments,
    dontPrint,
    showLegalData,
}: Props) {
    const { configs } = useConfigs()
    const printRef = useRef<HTMLElement>()

    useEffect(() => {
        if (!printRef.current) return

        const twoPages = false

        for (let child in printRef.current.children) {
            if (isNaN(Number(child))) continue
            const element = printRef.current.children[child] as HTMLElement

            const a4HeightPx = 1122
            let heightInVh = (element.offsetHeight / a4HeightPx) * 100

            if (heightInVh < 50 && !twoPages) {
                heightInVh = 50
            } else {
                heightInVh = 100 * (Math.floor((heightInVh - 1) / 100) + 1)
            }

            element.style.height = `${heightInVh}vh`
        }

        if (!dontPrint) {
            printRef.current!.style.visibility = 'hidden'
            printRef.current!.style.position = 'fixed'

            setTimeout(() => {
                printRef.current!.style.position = 'absolute'
                printRef.current!.className = 'printable'
                window.scroll(0, 0)
                window.print()
                handleClose()
            }, 100)
        }
    }, [])

    return (
        <Box id="note" ref={printRef}>
            {payments.map((payment) => (
                <>
                    {Array.from({
                        length: !dontPrint ? configs.notesQnt : 1,
                    }).map(() => (
                        <PaymentNote
                            payment={payment}
                            showLegalData={showLegalData}
                        />
                    ))}
                </>
            ))}
        </Box>
    )
}
